import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Invoice, BillingSettings, ResellerUser } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDate, formatCurrency } from '@/lib/utils';
import {
  Loader2,
  Settings,
  FileText,
  Users,
  Save,
  AlertCircle,
  CheckCircle,
  Receipt,
} from 'lucide-react';

export function AdminBillingPage() {
  const [activeTab, setActiveTab] = useState('settings');
  const [settings, setSettings] = useState<BillingSettings | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [resellers, setResellers] = useState<ResellerUser[]>([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
  const [isLoadingResellers, setIsLoadingResellers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Form state
  const [wholesalePercent, setWholesalePercent] = useState<string>('70');

  useEffect(() => {
    loadSettings();
    loadInvoices();
    loadResellers();
  }, []);

  const loadSettings = async () => {
    setIsLoadingSettings(true);
    try {
      const response = await api.getBillingSettings();
      setSettings(response);
      setWholesalePercent(String(response.wholesale_percent_default_display));
    } catch (err) {
      console.error('Failed to load billing settings:', err);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const loadInvoices = async () => {
    setIsLoadingInvoices(true);
    try {
      const response = await api.getAdminInvoices();
      setInvoices(response.invoices);
    } catch (err) {
      console.error('Failed to load invoices:', err);
    } finally {
      setIsLoadingInvoices(false);
    }
  };

  const loadResellers = async () => {
    setIsLoadingResellers(true);
    try {
      const response = await api.getUsers('active');
      // Filter to only resellers
      // Wholesale overrides only apply to reseller partners, not affiliates
      setResellers(response.users.filter((u) => u.role === 'partner' && u.partner_type !== 'affiliate'));
    } catch (err) {
      console.error('Failed to load resellers:', err);
    } finally {
      setIsLoadingResellers(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    const percentValue = parseInt(wholesalePercent, 10);
    if (isNaN(percentValue) || percentValue < 1 || percentValue > 100) {
      setError('Wholesale percent must be between 1 and 100');
      setIsSaving(false);
      return;
    }

    try {
      const response = await api.updateBillingSettings(percentValue);
      setSettings({
        wholesale_percent_default: response.wholesale_percent_default,
        wholesale_percent_default_display: response.wholesale_percent_default_display,
      });
      setSuccess('Billing settings updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: unknown) {
      console.error('Failed to save settings:', err);
      const apiError = err as { error?: string };
      setError(apiError.error || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reseller Billing</h1>
          <p className="text-gray-600 mt-1">Configure wholesale billing settings and view invoices</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </TabsTrigger>
            <TabsTrigger value="resellers" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Reseller Overrides
            </TabsTrigger>
            <TabsTrigger value="invoices" className="flex items-center gap-2">
              <Receipt className="h-4 w-4" />
              All Invoices
            </TabsTrigger>
          </TabsList>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Wholesale Billing</CardTitle>
                <CardDescription>
                  Configure the global wholesale rate for reseller billing
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingSettings ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-abby-primary" />
                  </div>
                ) : (
                  <div className="space-y-6 max-w-md">
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                        <AlertCircle className="h-4 w-4" />
                        {error}
                      </div>
                    )}
                    {success && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
                        <CheckCircle className="h-4 w-4" />
                        {success}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="wholesalePercent">Global wholesale rate (%)</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="wholesalePercent"
                          type="number"
                          min="1"
                          max="100"
                          value={wholesalePercent}
                          onChange={(e) => setWholesalePercent(e.target.value)}
                          className="w-24"
                        />
                        <span className="text-gray-500">%</span>
                      </div>
                      <p className="text-sm text-gray-500">
                        Resellers pay this percent of retail line items (recurring, overage, one-time, credits).
                      </p>
                    </div>

                    <Button onClick={handleSaveSettings} disabled={isSaving}>
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Save Settings
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reseller Overrides Tab */}
          <TabsContent value="resellers" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Reseller Wholesale Overrides</CardTitle>
                <CardDescription>
                  Set per-reseller wholesale rates that override the global default
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingResellers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-abby-primary" />
                  </div>
                ) : resellers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No resellers found.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {resellers.map((reseller) => (
                      <ResellerWholesaleRow
                        key={reseller.id}
                        reseller={reseller}
                        globalDefault={settings?.wholesale_percent_default_display || 70}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Invoices Tab */}
          <TabsContent value="invoices" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>All Invoices</CardTitle>
                <CardDescription>
                  View all issued invoices across all resellers
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingInvoices ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-abby-primary" />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">No invoices issued yet.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-3 font-medium">Invoice #</th>
                          <th className="pb-3 font-medium">Reseller</th>
                          <th className="pb-3 font-medium">Period</th>
                          <th className="pb-3 font-medium">Issued</th>
                          <th className="pb-3 font-medium text-right">Total</th>
                          <th className="pb-3 font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {invoices.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-gray-50">
                            <td className="py-3 font-medium text-gray-900">
                              {invoice.invoice_number}
                            </td>
                            <td className="py-3 text-gray-600">
                              {invoice.reseller_company_name || 'Unknown'}
                            </td>
                            <td className="py-3 text-gray-600">
                              {formatPeriod(invoice.period_start)}
                            </td>
                            <td className="py-3 text-gray-600">
                              {formatDate(invoice.issued_at)}
                            </td>
                            <td className="py-3 text-right font-medium text-gray-900">
                              {formatCurrency(invoice.total_wholesale)}
                            </td>
                            <td className="py-3">
                              <InvoiceStatusBadge status={invoice.status} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function ResellerWholesaleRow({
  reseller,
  globalDefault,
}: {
  reseller: ResellerUser;
  globalDefault: number;
}) {
  const [override, setOverride] = useState<string>('');
  const [effectivePercent, setEffectivePercent] = useState<number>(globalDefault);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    loadOverride();
  }, []);

  const loadOverride = async () => {
    if (!reseller.reseller_id) return;
    setIsLoading(true);
    try {
      const response = await api.getResellerWholesalePercent(reseller.reseller_id);
      setOverride(
        response.wholesale_percent_override_display !== null
          ? String(response.wholesale_percent_override_display)
          : ''
      );
      setEffectivePercent(response.effective_wholesale_percent_display);
    } catch (err) {
      console.error('Failed to load reseller override:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!reseller.reseller_id) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    let percentValue: number | null = null;
    if (override.trim() !== '') {
      percentValue = parseInt(override, 10);
      if (isNaN(percentValue) || percentValue < 1 || percentValue > 100) {
        setError('Must be 1-100 or blank for default');
        setIsSaving(false);
        return;
      }
    }

    try {
      const response = await api.updateResellerWholesalePercent(reseller.reseller_id, percentValue);
      setEffectivePercent(response.effective_wholesale_percent_display);
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err: unknown) {
      console.error('Failed to save override:', err);
      const apiError = err as { error?: string };
      setError(apiError.error || 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  if (!reseller.reseller_id) return null;

  return (
    <div className="flex items-center gap-4 p-3 border rounded-lg">
      <div className="flex-1">
        <p className="font-medium text-gray-900">{reseller.company_name || reseller.email}</p>
        <p className="text-sm text-gray-500">Effective: {effectivePercent}%</p>
      </div>
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      ) : (
        <>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="1"
              max="100"
              placeholder={String(globalDefault)}
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              className="w-20"
            />
            <span className="text-gray-500 text-sm">%</span>
          </div>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
          </Button>
          {error && <span className="text-xs text-red-600">{error}</span>}
          {success && <span className="text-xs text-green-600">{success}</span>}
        </>
      )}
    </div>
  );
}

function formatPeriod(periodStart: string): string {
  // period_start is a calendar date (YYYY-MM-DD); parse it as such, not as UTC midnight
  const [year, month] = periodStart.substring(0, 10).split('-');
  const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function InvoiceStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    issued: 'bg-blue-100 text-blue-800',
    sent: 'bg-purple-100 text-purple-800',
    paid: 'bg-green-100 text-green-800',
    void: 'bg-gray-100 text-gray-800',
  };

  const labels: Record<string, string> = {
    issued: 'Issued',
    sent: 'Sent',
    paid: 'Paid',
    void: 'Void',
  };

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.issued}`}>
      {labels[status] || status}
    </span>
  );
}
